import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { RallyCampEntry, RallyDetails, RallySummary } from "../types/rally";
import { castleKeys } from "./castle";

export const rallyKeys = {
  camps: ["rally", "camps"] as const,
  active: ["rally", "active"] as const,
  details: (id: string) => ["rally", "details", id] as const,
};

export function useRallyCamps(enabled: boolean) {
  return useQuery({
    queryKey: rallyKeys.camps,
    queryFn: async () => {
      const res = await api.get<RallyCampEntry[]>("/rally-camps");
      return res.data;
    },
    enabled,
    refetchInterval: 30_000,
  });
}

export function useActiveRallies() {
  return useQuery({
    queryKey: rallyKeys.active,
    queryFn: async () => {
      const res = await api.get<RallySummary[]>("/rally");
      return res.data;
    },
    refetchInterval: 5_000,
  });
}

export function useRallyDetails(id: string | null) {
  return useQuery({
    queryKey: id ? rallyKeys.details(id) : ["rally", "details", "none"],
    queryFn: async () => {
      const res = await api.get<RallyDetails>(`/rally/${id}`);
      return res.data;
    },
    enabled: !!id,
    refetchInterval: 5_000,
  });
}

export function useStartRally() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      campId,
      units,
      heroIds,
      gatheringSeconds,
    }: {
      campId: string;
      units: Record<string, number>;
      heroIds: string[];
      gatheringSeconds: number;
    }) => {
      const res = await api.post("/rally/start", { campId, units, heroIds, gatheringSeconds });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rallyKeys.active });
      qc.invalidateQueries({ queryKey: rallyKeys.camps });
      qc.invalidateQueries({ queryKey: castleKeys.state });
    },
  });
}

export function useJoinRally() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      rallyId,
      units,
      heroIds,
    }: {
      rallyId: string;
      units: Record<string, number>;
      heroIds: string[];
    }) => {
      const res = await api.post(`/rally/${rallyId}/join`, { units, heroIds });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rallyKeys.active });
      qc.invalidateQueries({ queryKey: castleKeys.state });
    },
  });
}

export function useCancelRally() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rallyId: string) => {
      const res = await api.post(`/rally/${rallyId}/cancel`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rallyKeys.active });
      qc.invalidateQueries({ queryKey: castleKeys.state });
    },
  });
}
